// @ts-check
import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminCollectionService from '../../services/adminCollectionService';
import { reportBulkResult, runBulk } from '../../services/bulkService';
import Modal from '../ui/Modal';

const BULK_PATH = '/api/admin/products/bulk';
const INPUT = 'w-full border rounded-lg px-3 py-2 text-sm';

/**
 * Shell + footer shared by the three product bulk dialogs. Apply calls the bulk endpoint with
 * `buildRequest()`, toasts the result, and calls onDone on success.
 * @param {{ title: string, count: number, ids: string[], onClose: () => void, onDone: () => void,
 *   canApply: boolean, verb: string, buildRequest: () => { action: string, params: object },
 *   children: any, extraActions?: any }} props
 */
const DialogShell = ({
  title,
  count,
  ids,
  onClose,
  onDone,
  canApply,
  verb,
  children,
  extraActions = null,
  buildRequest,
}) => {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      const { action, params } = buildRequest();
      const result = await runBulk(BULK_PATH, { ids, action, params });
      reportBulkResult(toast, result, verb, 'product');
      onDone();
    } catch (error) {
      toast.error(error.message || 'Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={busy ? undefined : onClose} maxWidth="md" fullHeight={false}>
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">
          {title} <span className="text-sm font-normal text-muted-foreground">({count} selected)</span>
        </h2>
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm border rounded-lg">
            Cancel
          </button>
          {extraActions}
          <button
            type="button"
            onClick={apply}
            disabled={!canApply || busy}
            className="px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg disabled:opacity-50"
          >
            {busy ? 'Working...' : 'Apply'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const CLEAR = '__clear__';

/** Set every selected product's collection, or clear it. A product belongs to one collection. */
export const BulkCollectionDialog = (props) => {
  const toast = useToast();
  const [collections, setCollections] = useState([]);
  const [choice, setChoice] = useState('');

  useEffect(() => {
    adminCollectionService
      .getCollections()
      .then(setCollections)
      .catch((error) => toast.error(error.message || 'Failed to load collections'));
  }, [toast]);

  return (
    <DialogShell
      {...props}
      title="Set collection"
      verb="updated"
      canApply={choice !== ''}
      buildRequest={() => ({ action: 'set_collection', params: { collection_id: choice === CLEAR ? null : choice } })}
    >
      <p className="text-sm text-muted-foreground">
        A product belongs to one collection, so this replaces its current one.
      </p>
      <select aria-label="Collection" className={INPUT} value={choice} onChange={(e) => setChoice(e.target.value)}>
        <option value="" disabled>
          Choose a collection...
        </option>
        <option value={CLEAR}>No collection (clear it)</option>
        {collections.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </DialogShell>
  );
};

/** Set HS code and/or parcel weight. Blank fields are left as they are. */
export const BulkShippingDialog = (props) => {
  const [hsn, setHsn] = useState('');
  const [weight, setWeight] = useState('');
  const hasWeight = weight !== '' && Number(weight) > 0;

  return (
    <DialogShell
      {...props}
      title="Set shipping data"
      verb="updated"
      canApply={hsn.trim() !== '' || hasWeight}
      buildRequest={() => ({
        action: 'set_shipping',
        params: { ...(hsn.trim() && { hsn_code: hsn.trim() }), ...(hasWeight && { shipping_weight_kg: weight }) },
      })}
    >
      <p className="text-sm text-muted-foreground">Leave a field blank to keep each product's current value.</p>
      <label className="block text-sm text-foreground/80">
        HS code
        <input className={`${INPUT} mt-1`} maxLength={20} value={hsn} onChange={(e) => setHsn(e.target.value)} />
      </label>
      <label className="block text-sm text-foreground/80">
        Parcel weight (kg)
        <input
          className={`${INPUT} mt-1`}
          type="number"
          min="0"
          step="0.001"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
      </label>
    </DialogShell>
  );
};

const money = (v) => (v == null ? '-' : `₹${Number(v).toLocaleString('en-IN')}`);

/** Raise or lower prices by a percentage or an amount, with a before/after preview first. */
export const BulkPriceDialog = (props) => {
  const toast = useToast();
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [applyToSale, setApplyToSale] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const valid = value !== '' && Number(value) !== 0;

  const change = (setter) => (e) => {
    setter(e.target.value);
    setPreview(null); // an old preview no longer matches the inputs
  };

  const request = (dryRun) => ({
    action: 'change_price',
    params: { mode, value, apply_to_sale: applyToSale, ...(dryRun && { dry_run: true }) },
  });

  const showPreview = async () => {
    setPreviewing(true);
    try {
      setPreview(await runBulk(BULK_PATH, { ids: props.ids, ...request(true) }));
    } catch (error) {
      toast.error(error.message || 'Failed to build preview');
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <DialogShell
      {...props}
      title="Change price"
      verb="repriced"
      canApply={valid && preview !== null && preview.preview.length > 0}
      buildRequest={() => request(false)}
      extraActions={
        <button
          type="button"
          onClick={showPreview}
          disabled={!valid || previewing}
          className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50"
        >
          {previewing ? 'Loading...' : 'Preview'}
        </button>
      }
    >
      <div className="flex gap-2">
        <select aria-label="Mode" className={INPUT} value={mode} onChange={change(setMode)}>
          <option value="percent">Percent (%)</option>
          <option value="amount">Amount (₹)</option>
        </select>
        <input
          aria-label="Change"
          className={INPUT}
          type="number"
          step="any"
          placeholder="e.g. -10 lowers, 10 raises"
          value={value}
          onChange={change(setValue)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-foreground/80">
        <input
          type="checkbox"
          checked={applyToSale}
          onChange={(e) => {
            setApplyToSale(e.target.checked);
            setPreview(null);
          }}
        />
        Also change the sale price
      </label>
      {preview && (
        <div className="border rounded-md text-xs max-h-48 overflow-y-auto">
          {preview.preview.slice(0, 10).map((row) => (
            <div key={row.id} className="flex justify-between gap-3 px-3 py-1.5 border-b last:border-0">
              <span className="truncate">{row.name}</span>
              <span className="whitespace-nowrap">
                {money(row.price_before)} &rarr; {money(row.price_after)}
              </span>
            </div>
          ))}
          {preview.preview.length > 10 && (
            <div className="px-3 py-1.5 text-muted-foreground">and {preview.preview.length - 10} more</div>
          )}
          {preview.failed.length > 0 && (
            <div className="px-3 py-1.5 text-red-600 dark:text-red-400">
              {preview.failed.length} will be skipped: {[...new Set(preview.failed.map((f) => f.reason))].join('; ')}
            </div>
          )}
        </div>
      )}
    </DialogShell>
  );
};
